import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, tokens } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { Button, Card, CardHeader, Detail, Field, Input, PageHeader, useToast } from '../components/ui';

/**
 * The account: whose sign-in this is — a contact's, or the company's own — which
 * company and username, and changing the password. A new password signs every
 * other device out; this one carries on.
 */
export function AccountPage() {
  const { t, locale } = useI18n();
  const { account } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({ current: '', next: '', again: '' });
  const [problem, setProblem] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      api.post<{ accessToken: string; refreshToken: string }>('/auth/change-password', {
        currentPassword: form.current,
        newPassword: form.next,
      }),
    onSuccess: (fresh) => {
      tokens.set(fresh.accessToken, fresh.refreshToken);
      setForm({ current: '', next: '', again: '' });
      toast.success(t('account.changed'));
    },
    onError: (e: Error) => setProblem(e.message),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (form.next !== form.again) {
      setProblem(t('account.mismatch'));
      return;
    }
    change.mutate();
  };

  if (!account) return null;

  return (
    <>
      <PageHeader title={t('account.title')} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <dl className="divide-y divide-[var(--line)]">
            {/* A contact's own sign-in, or the company's (2026-10-11). */}
            <Detail label={t('account.signedInAs')}>
              {account.contact
                ? [account.contact.name, account.contact.jobTitle].filter(Boolean).join(' — ')
                : t('account.companySignIn')}
            </Detail>
            <Detail label={t('account.company')}>
              {locale === 'ar' && account.client.companyNameAr ? account.client.companyNameAr : account.client.companyName}
            </Detail>
            <Detail label={t('account.username')}>
              <bdi dir="ltr">{account.username}</bdi>
            </Detail>
            {account.lastLoginAt && (
              <Detail label={t('account.lastSignIn')}>{formatDateTime(account.lastLoginAt, locale)}</Detail>
            )}
          </dl>
        </Card>

        <Card>
          <CardHeader title={t('account.changePassword')} />
          <form onSubmit={submit} className="space-y-4">
            <Field label={t('account.currentPassword')} required>
              <Input
                type="password"
                autoComplete="current-password"
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                dir="ltr"
                required
              />
            </Field>
            <Field label={t('account.newPassword')} required hint={t('account.passwordHint')}>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                minLength={8}
                dir="ltr"
                required
              />
            </Field>
            <Field label={t('account.confirmPassword')} required>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.again}
                onChange={(e) => setForm({ ...form, again: e.target.value })}
                minLength={8}
                dir="ltr"
                required
              />
            </Field>
            {problem && (
              <p role="alert" className="rounded-md border border-error/30 bg-error/10 px-3 py-2 text-body-sm text-error-fg dark:text-error-dark">
                {problem}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={change.isPending}
              disabled={!form.current || form.next.length < 8 || !form.again}
            >
              {t('account.changePassword')}
            </Button>
          </form>
        </Card>
      </div>
    </>
  );
}
